export const CONVERSATION_STORAGE_KEY = 'horatix.conversationId'
export const CONVERSATION_QUERY_PARAM = 'c'
export const WATCH_BUILDER_PATH = '/watch-builder'

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

export function parseConversationId(value: string | null | undefined): string | null {
  const trimmed = value?.trim()
  if (!trimmed || !UUID_RE.test(trimmed)) return null
  return trimmed.toLowerCase()
}

export function readStoredConversationId(): string | null {
  try {
    return parseConversationId(window.localStorage.getItem(CONVERSATION_STORAGE_KEY))
  } catch {
    return null
  }
}

export function persistConversationId(conversationId: string): void {
  const parsed = parseConversationId(conversationId)
  if (!parsed) return
  try {
    window.localStorage.setItem(CONVERSATION_STORAGE_KEY, parsed)
  } catch {
    // Ignore quota / private-mode failures; resume still works via the URL.
  }
}

export function clearStoredConversationId(): void {
  try {
    window.localStorage.removeItem(CONVERSATION_STORAGE_KEY)
  } catch {
    // Ignore storage failures.
  }
}

export function conversationPermalink(conversationId: string, origin = window.location.origin): string {
  const parsed = parseConversationId(conversationId)
  const id = parsed ?? conversationId
  return `${origin}${WATCH_BUILDER_PATH}?${CONVERSATION_QUERY_PARAM}=${encodeURIComponent(id)}`
}

export function resolveResumeConversationId(
  pathname: string,
  searchParams: URLSearchParams,
): string | null {
  if (pathname === WATCH_BUILDER_PATH) {
    const fromUrl = parseConversationId(searchParams.get(CONVERSATION_QUERY_PARAM))
    if (fromUrl) return fromUrl
  }
  return readStoredConversationId()
}

export async function copyTextToClipboard(text: string): Promise<void> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text)
      return
    }
  } catch {
    // Async work can drop user-activation; fall back to execCommand.
  }

  const textarea = document.createElement('textarea')
  textarea.value = text
  textarea.setAttribute('readonly', '')
  textarea.style.position = 'fixed'
  textarea.style.top = '0'
  textarea.style.left = '0'
  textarea.style.opacity = '0'
  document.body.appendChild(textarea)
  textarea.focus()
  textarea.select()
  textarea.setSelectionRange(0, text.length)
  const copied = document.execCommand('copy')
  document.body.removeChild(textarea)
  if (!copied) {
    throw new Error('Could not copy link')
  }
}
